/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import * as logger from '@ion/core/src/logger/logger';
import {normalize} from '@ion/core/src/utils/StringUtil';

/**
 * When `true`, verbose logging will be enabled, thus all logs will be included.
 */
let verbose = false;

/**
 * Defines the mapping of GraphQL Framework Log Message functions.
 */
export const messages = {
  initializeSuccess : (
    serviceURL?: string, appKey?: string, verboseLogging?: boolean): string => (
    `Framework Initialized. Application: ${appKey}, URL: ${serviceURL}, Verbose Logging: ${verboseLogging}`
  ),
  initializeError : (): string => (
    'Framework has previously been initialized'
  ),
  authTokenSuccess : (): string => (`
    "setAuthToken" configuration successfully invoked. JWT Token has been applied to all request headers.
  `),
  authTokenError : (): string => (`
    "setAuthToken" configuration invoked prematurely, the client has yet to have been configured.
    You must first invoke the "setGraphQLConfig" initialization function prior to invoking
    "setAuthToken".
  `),
  request : (query: string): string => (
    `Request issued: ${query}`
  ),
  response : (results: any): string => (
    `Request issued: ${JSON.stringify(results)}`
  ),
  error : (error): string => (
    `Request Failed: ${JSON.stringify(error)}`
  )
};

/**
 * Allows for enabling verbose logging (defaults to `false`). When `true`, all
 * logs will be included.
 */
export const enableVerboseLogging = (enable = false) => {
  verbose = enable;
};

/**
 * Provides the logger for outputting the results of the framework initialization
 * process.
 */
export const initialize = (serviceURL?: string, appKey?: string, verboseLogging?: boolean) => {
  if (serviceURL && appKey) {
    log(messages.initializeSuccess(serviceURL, appKey, verboseLogging));
  } else {
    log(messages.initializeError(), true);
  }
};

/**
 * Provides the logger for outputting the results of invoking the `setAuthToken`
 * configuration.
 */
export const setAuthToken = (isValid = true) => {
  log(isValid ? messages.authTokenSuccess() : messages.authTokenError(), !isValid);
};

/**
 * Provides the logger for outputting the query issued to a GraphQL Request.
 * Omitted when `enableVerboseLogging` is set to `false`.
 */
export const request = (query: string) => {
  if (verbose) {
    log(messages.request(query));
  }
};

/**
 * Provides the logger for outputting the results returned from a GraphQL Response.
 * Omitted when `enableVerboseLogging` is set to `false`.
 */
export const response = (results: any) => {
  if (verbose) {
    log(messages.response(results));
  }
};

/**
 * Provides the logger for outputting the results returned from a GraphQL Response.
 * Omitted when `enableVerboseLogging` is set to `false`.
 */
export const error = (err: any) => {
  if (verbose) {
    log(messages.error(err), true);
  }
};

/**
 * Provides the default logging facility through which all GraphQL logs are generated
 * and emitted.
 *
 * @param msg     Defines the message to be logged to the underlying logger.
 * @param isError When `true`, indicates that the error logger is to be used.
 */
const log = (message: string, isError = false) => {
  logger[isError ? 'error' : 'info'](`GraphQL API: ${normalize(message)}`);
};
